import React, { useState, useEffect } from 'react';
import { 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Building, 
  Globe, 
  Save, 
  Edit3, 
  X,
  Camera,
  Shield,
  Calendar,
  FileText,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../../../config/api';

const ProfileSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [ngoData, setNgoData] = useState(null);
  const [formData, setFormData] = useState({
    organizationName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    website: '',
    description: '',
    foundedYear: '',
    registrationNumber: '',
    taxId: '',
    contactPerson: {
      name: '',
      position: '',
      email: '',
      phone: ''
    },
    socialMedia: {
      facebook: '',
      twitter: '',
      linkedin: '',
      instagram: ''
    }
  });
  const [profileImage, setProfileImage] = useState(null);
  const [previewImage, setPreviewImage] = useState('');

  useEffect(() => {
    fetchNgoProfile();
  }, []);

  const fetchNgoProfile = async () => {
    try {
      setLoading(true);
      console.log('Fetching NGO profile...');
      console.log('API base URL:', api.defaults.baseURL);
      console.log('Request URL:', '/ngos/profile');
      
      // First test the profile-test route to see if routing is working
      try {
        const testResponse = await api.get('/ngos/profile-test');
        console.log('Profile test route response:', testResponse);
      } catch (testError) {
        console.log('Profile test route failed:', testError);
      }
      
      const response = await api.get('/ngos/profile');
      console.log('Profile response:', response);
      
      if (response.data?.success) {
        const ngo = response.data.data;
        console.log('NGO data received:', ngo);
        setNgoData(ngo);
        
        // Set form data - map from existing NGO registration fields to profile fields
        setFormData({
          organizationName: ngo.organizationName || ngo.name || '',
          email: ngo.email || '',
          phone: ngo.phone || ngo.phoneNumber || '',
          address: ngo.address || '',
          city: ngo.city || '',
          state: ngo.state || '',
          pincode: ngo.pincode || '',
          website: ngo.website || '',
          description: ngo.description || '',
          foundedYear: ngo.foundedYear || (ngo.startDate ? new Date(ngo.startDate).getFullYear().toString() : ''),
          registrationNumber: ngo.registrationNumber || '',
          taxId: ngo.taxId || '',
          contactPerson: {
            name: ngo.contactPerson?.name || ngo.personalName || '',
            position: ngo.contactPerson?.position || '',
            email: ngo.contactPerson?.email || '',
            phone: ngo.contactPerson?.phone || ngo.personalPhone || ''
          },
          socialMedia: {
            facebook: ngo.socialMedia?.facebook || '',
            twitter: ngo.socialMedia?.twitter || '',
            linkedin: ngo.socialMedia?.linkedin || '',
            instagram: ngo.socialMedia?.instagram || ''
          }
        });

        if (ngo.profilePicture) {
          setPreviewImage(ngo.profilePicture);
        } else if (ngo.logo) {
          setPreviewImage(ngo.logo);
        }
      } else {
        console.log('Response not successful:', response.data);
      }
    } catch (error) {
      console.error('Error fetching NGO profile:', error);
      console.error('Error response:', error.response);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      
      if (error.response?.status === 401) {
        toast.error('Authentication failed. Please login again.');
        // Redirect to NGO login
        window.location.href = '/ngo/login';
      } else if (error.response?.status === 404) {
        toast.error('Profile endpoint not found. Please check server configuration.');
      } else {
        toast.error('Failed to load profile information. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleContactPersonChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      contactPerson: {
        ...prev.contactPerson,
        [field]: value
      }
    }));
  };

  const handleSocialMediaChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      socialMedia: {
        ...prev.socialMedia,
        [field]: value
      }
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast.error('Image size should be less than 5MB');
        return;
      }
      
      setProfileImage(file);
      const reader = new FileReader();
      reader.onload = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setProfileImage(null);
    setPreviewImage(ngoData?.profilePicture || '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      
      const submitData = new FormData();
      
      // Basic fields
      Object.keys(formData).forEach(key => {
        if (key === 'contactPerson' || key === 'socialMedia') {
          submitData.append(key, JSON.stringify(formData[key]));
        } else {
          submitData.append(key, formData[key]);
        }
      });
      
      // Profile picture if changed
      if (profileImage) {
        submitData.append('profilePicture', profileImage);
      }
      
      const response = await api.put('/ngos/profile', submitData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data?.success) {
        toast.success('Profile updated successfully!');
        setEditing(false);
        setProfileImage(null);
        await fetchNgoProfile(); // Refresh data
      }
      
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'verified': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'pending': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'rejected': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'verified': return <CheckCircle className="w-4 h-4" />;
      case 'pending': return <AlertCircle className="w-4 h-4" />;
      case 'rejected': return <X className="w-4 h-4" />;
      default: return <AlertCircle className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 dark:text-white">Profile Settings</h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              Manage your NGO profile information and settings
            </p>
          </div>
          
          <div className="flex items-center space-x-3">
            {ngoData && (
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(ngoData.status)}`}>
                {getStatusIcon(ngoData.status)}
                <span className="ml-2 capitalize">{ngoData.status || 'pending'}</span>
              </span>
            )}
            
            {!editing ? (
              <button
                onClick={() => setEditing(true)}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Edit3 className="w-4 h-4 mr-2" />
                Edit Profile
              </button>
            ) : (
              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    setEditing(false);
                    setProfileImage(null);
                    setPreviewImage(ngoData?.profilePicture || '');
                  }}
                  className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={saving}
                  className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Registration Information - Read Only */}
        {ngoData && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
            <h3 className="text-lg font-medium text-blue-900 dark:text-blue-100 mb-4 flex items-center">
              <CheckCircle className="w-5 h-5 mr-2" />
              Registration Information
            </h3>
            <p className="text-sm text-blue-700 dark:text-blue-300 mb-4">
              This information was provided during registration and cannot be edited here.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                <label className="block text-xs font-medium text-blue-600 dark:text-blue-400 mb-1">
                  Organization Name
                </label>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {ngoData.name || 'Not provided'}
                </p>
              </div>
              
              <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                <label className="block text-xs font-medium text-blue-600 dark:text-blue-400 mb-1">
                  Registration Date
                </label>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {ngoData.registrationDate ? new Date(ngoData.registrationDate).toLocaleDateString() : 'Not provided'}
                </p>
              </div>
              
              <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                <label className="block text-xs font-medium text-blue-600 dark:text-blue-400 mb-1">
                  Start Date
                </label>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {ngoData.startDate ? new Date(ngoData.startDate).toLocaleDateString() : 'Not provided'}
                </p>
              </div>
              
              <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                <label className="block text-xs font-medium text-blue-600 dark:text-blue-400 mb-1">
                  Contact Person
                </label>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {ngoData.personalName || 'Not provided'}
                </p>
              </div>
              
              <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                <label className="block text-xs font-medium text-blue-600 dark:text-blue-400 mb-1">
                  Contact Phone
                </label>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {ngoData.personalPhone || 'Not provided'}
                </p>
              </div>
              
              <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                <label className="block text-xs font-medium text-blue-600 dark:text-blue-400 mb-1">
                  Status
                </label>
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(ngoData.status)}`}>
                  {getStatusIcon(ngoData.status)}
                  <span className="ml-1 capitalize">{ngoData.status || 'pending'}</span>
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Profile Picture Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
            <Camera className="w-5 h-5 mr-2" />
            Profile Picture
          </h3>
          
                      <div className="flex items-center space-x-6">
              <div className="relative">
                <img
                  src={previewImage || '/images/r.png'}
                  alt="NGO Profile"
                  className="w-24 h-24 rounded-full object-cover border-4 border-gray-200 dark:border-gray-700"
                  onError={(e) => {
                    if (e.target) {
                      e.target.src = '/images/r.png';
                    }
                  }}
                />
                {profileImage && (
                  <button
                    type="button"
                    onClick={removeImage}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
                {ngoData?.logo && !previewImage && (
                  <div className="absolute -bottom-2 -right-2 bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
                    Logo
                  </div>
                )}
              </div>
            
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Upload New Picture
              </label>
              <div className="flex items-center space-x-3">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                  id="profile-image"
                  disabled={!editing}
                />
                <label
                  htmlFor="profile-image"
                  className={`flex items-center px-4 py-2 rounded-lg cursor-pointer transition-colors ${
                    editing 
                      ? 'bg-blue-600 text-white hover:bg-blue-700' 
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  <Camera className="w-4 h-4 mr-2" />
                  Choose Image
                </label>
                <span className="text-sm text-gray-500">
                  {profileImage ? profileImage.name : 'No file chosen'}
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Max size: 5MB. Supported formats: JPG, PNG, GIF
              </p>
            </div>
          </div>
        </div>

        {/* Basic Information */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
            <Building className="w-5 h-5 mr-2" />
            Basic Information
          </h3>
          
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Organization Name *
                </label>
                <input
                  type="text"
                  name="organizationName"
                  value={formData.organizationName}
                  onChange={handleInputChange}
                  disabled={!editing}
                  required
                  placeholder={ngoData?.name ? `Current: ${ngoData.name}` : 'Enter organization name'}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                {ngoData?.name && (
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                    Registration name: {ngoData.name}
                  </p>
                )}
              </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Email *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                disabled={!editing}
                required
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>
            
                          <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Phone *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  disabled={!editing}
                  required
                  placeholder={ngoData?.phoneNumber ? `Current: ${ngoData.phoneNumber}` : 'Enter phone number'}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                {ngoData?.phoneNumber && (
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                    Registration phone: {ngoData.phoneNumber}
                  </p>
                )}
              </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Website
              </label>
              <input
                type="url"
                name="website"
                value={formData.website}
                onChange={handleInputChange}
                disabled={!editing}
                placeholder="https://example.com"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Founded Year
              </label>
              <input
                type="number"
                name="foundedYear"
                value={formData.foundedYear}
                onChange={handleInputChange}
                disabled={!editing}
                min="1800"
                max={new Date().getFullYear()}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Registration Number
              </label>
              <input
                type="text"
                name="registrationNumber"
                value={formData.registrationNumber}
                onChange={handleInputChange}
                disabled={!editing}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Tax ID
              </label>
              <input
                type="text"
                name="taxId"
                value={formData.taxId}
                onChange={handleInputChange}
                disabled={!editing}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>
          </div>
          
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              disabled={!editing}
              rows={4}
              placeholder="Describe your organization's mission, vision, and activities..."
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
          </div>
        </div>

        {/* Address Information */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
            <MapPin className="w-5 h-5 mr-2" />
            Address Information
          </h3>
          
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Address *
                </label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  disabled={!editing}
                  required
                  placeholder="Enter complete address"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
              </div>
            
                          <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  City *
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  disabled={!editing}
                  required
                  placeholder={ngoData?.city ? `Current: ${ngoData.city}` : 'Enter city'}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                {ngoData?.city && (
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                    Registration city: {ngoData.city}
                  </p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  State *
                </label>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  disabled={!editing}
                  required
                  placeholder={ngoData?.state ? `Current: ${ngoData.state}` : 'Enter state'}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                {ngoData?.state && (
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                    Registration state: {ngoData.state}
                  </p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Pincode *
                </label>
                <input
                  type="text"
                  name="pincode"
                  value={formData.pincode}
                  onChange={handleInputChange}
                  disabled={!editing}
                  required
                  placeholder={ngoData?.pincode ? `Current: ${ngoData.pincode}` : 'Enter pincode'}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                {ngoData?.pincode && (
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                    Registration pincode: {ngoData.pincode}
                  </p>
                )}
              </div>
          </div>
        </div>

        {/* Contact Person */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
            <User className="w-5 h-5 mr-2" />
            Primary Contact Person
          </h3>
          
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Contact Name
                </label>
                <input
                  type="text"
                  value={formData.contactPerson.name}
                  onChange={(e) => handleContactPersonChange('name', e.target.value)}
                  disabled={!editing}
                  placeholder={ngoData?.personalName ? `Current: ${ngoData.personalName}` : 'Enter contact person name'}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                {ngoData?.personalName && (
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                    Registration contact: {ngoData.personalName}
                  </p>
                )}
              </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Position
              </label>
              <input
                type="text"
                value={formData.contactPerson.position}
                onChange={(e) => handleContactPersonChange('position', e.target.value)}
                disabled={!editing}
                placeholder="e.g., Director, Manager"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Contact Email
              </label>
              <input
                type="email"
                value={formData.contactPerson.email}
                onChange={(e) => handleContactPersonChange('email', e.target.value)}
                disabled={!editing}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>
            
                          <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Contact Phone
                </label>
                <input
                  type="tel"
                  value={formData.contactPerson.phone}
                  onChange={(e) => handleContactPersonChange('phone', e.target.value)}
                  disabled={!editing}
                  placeholder={ngoData?.personalPhone ? `Current: ${ngoData.personalPhone}` : 'Enter contact phone number'}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                {ngoData?.personalPhone && (
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                    Registration phone: {ngoData.personalPhone}
                  </p>
                )}
              </div>
          </div>
        </div>

        {/* Social Media */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
            <Globe className="w-5 h-5 mr-2" />
            Social Media Links
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Facebook
              </label>
              <input
                type="url"
                value={formData.socialMedia.facebook}
                onChange={(e) => handleSocialMediaChange('facebook', e.target.value)}
                disabled={!editing}
                placeholder="https://facebook.com/yourpage"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Twitter
              </label>
              <input
                type="url"
                value={formData.socialMedia.twitter}
                onChange={(e) => handleSocialMediaChange('twitter', e.target.value)}
                disabled={!editing}
                placeholder="https://twitter.com/yourhandle"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                LinkedIn
              </label>
              <input
                type="url"
                value={formData.socialMedia.linkedin}
                onChange={(e) => handleSocialMediaChange('linkedin', e.target.value)}
                disabled={!editing}
                placeholder="https://linkedin.com/company/yourcompany"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Instagram
              </label>
              <input
                type="url"
                value={formData.socialMedia.instagram}
                onChange={(e) => handleSocialMediaChange('instagram', e.target.value)}
                disabled={!editing}
                placeholder="https://instagram.com/yourhandle"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>
          </div>
        </div>

        {/* Account Information */}
        {ngoData && (
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
              <Shield className="w-5 h-5 mr-2" />
              Account Information
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{ngoData.status || 'pending'}</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Verification Status</div>
              </div>
              
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {ngoData.createdAt ? new Date(ngoData.createdAt).toLocaleDateString() : 'N/A'}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Member Since</div>
              </div>
              
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">
                  {ngoData.lastLogin ? new Date(ngoData.lastLogin).toLocaleDateString() : 'N/A'}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Last Login</div>
              </div>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default ProfileSettings;
